import { signOut } from "firebase/auth";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { AnimatePresence, motion } from "framer-motion";
import {
	BarChart,
	BookOpen,
	HelpCircle,
	LogOut,
	Menu,
	MessageSquare,
	Settings as SettingsIcon,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import Auth from "./components/Auth";
import { useUser } from "./contexts/UserContext";
import { auth, db } from "./firebaseConfig";

const App = () => {
	const { user, setUser, loading } = useUser();
	const navigate = useNavigate();
	const location = useLocation();
	const [hasSubscription, setHasSubscription] = useState(false);
	const [isNavOpen, setIsNavOpen] = useState(false);
	const [freeEntriesLeft, setFreeEntriesLeft] = useState(0);
	const [freeSessionsLeft, setFreeSessionsLeft] = useState(0);

	useEffect(() => {
		const checkSubscription = async () => {
			if (user?.uid) {
				const userRef = doc(db, "Users", user.uid);
				const userDoc = await getDoc(userRef);
				if (userDoc.exists()) {
					const userData = userDoc.data();

					setFreeEntriesLeft(userData.freeEntriesLeft ?? 0);
					setFreeSessionsLeft(userData.freeSessionsLeft ?? 0);

					const now = new Date();
					const isActive = userData.subscriptionActive;
					const endDate = userData.subscriptionEndDate?.toDate();
					const trialEnd = userData.subscriptionTrialEnd?.toDate();
					const subscriptionStatus = userData.subscriptionStatus;

					// Check various conditions that would invalidate access
					const isTrialExpired = trialEnd && now > trialEnd;
					const isSubscriptionExpired = endDate && now > endDate;
					const isTrialEnded = subscriptionStatus === "trial_expired";

					// Allow access if subscription is active and not expired,
					// even if it's canceled (access continues until period end)
					const hasValidAccess =
						isActive &&
						!isTrialExpired &&
						!isSubscriptionExpired &&
						!isTrialEnded;

					setHasSubscription(hasValidAccess);
				} else {
					setHasSubscription(false);
				}
			}
		};

		checkSubscription();

		// Real-time listener for subscription changes
		if (user?.uid) {
			const userRef = doc(db, "Users", user.uid);
			const unsubscribe = onSnapshot(userRef, (doc) => {
				if (doc.exists()) {
					const userData = doc.data();
					const now = new Date();
					const isActive = userData.subscriptionActive;
					const endDate = userData.subscriptionEndDate?.toDate();
					const trialEnd = userData.subscriptionTrialEnd?.toDate();
					const subscriptionStatus = userData.subscriptionStatus;

					const isTrialExpired = trialEnd && now > trialEnd;
					const isSubscriptionExpired = endDate && now > endDate;
					const isTrialEnded = subscriptionStatus === "trial_expired";

					const hasValidAccess =
						isActive &&
						!isTrialExpired &&
						!isSubscriptionExpired &&
						!isTrialEnded;

					setHasSubscription(hasValidAccess);
				} else {
					setHasSubscription(false);
				}
			});

			return () => unsubscribe();
		}
	}, [user, location.pathname, navigate]);

	const handleNavItemClick = (path) => {
		navigate(path);
		setIsNavOpen(false);
	};

	const handleLogout = async () => {
		try {
			await signOut(auth);
			setUser(null);
		} catch (error) {
			console.error("Error signing out:", error);
		}
	};

	// Shutdown Notice Component
	const ShutdownNotice = () => (
		<div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
			<div className="flex items-center justify-center min-h-screen p-4">
				<div className="max-w-2xl mx-auto">
					<div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-gray-100">
						<div className="text-center">
							{/* Icon */}
							<div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full mb-6">
								<svg
									className="w-10 h-10 text-blue-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
							</div>

							{/* Title */}
							<h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
								Thank You for Being Part of Our Journey
							</h2>

							{/* Message */}
							<div className="space-y-4 text-gray-600 leading-relaxed">
								<p className="text-base">
									We regret to inform you that Milburn has
									discontinued operations and is no longer
									available.
								</p>
								<p className="text-base">
									To all our users: thank you for your
									continued support, trust, and for being part
									of our community. We're grateful for the
									opportunity to have served you on your
									mental health journey.
								</p>
								<p className="text-base">
									Your data and privacy remain important to us
									even as we wind down operations.
								</p>
							</div>

							{/* Action Button */}
							<div className="mt-8">
								<button
									onClick={handleLogout}
									className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
									<svg
										className="w-5 h-5 mr-2"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24">
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
										/>
									</svg>
									Sign Out
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);

	const NavBar = () => (
		<>
			{/* Mobile Menu Button */}
			<AnimatePresence>
				{!isNavOpen && (
					<motion.button
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="md:hidden fixed top-2 left-2 z-50 p-2 rounded-md bg-transparent"
						onClick={() => setIsNavOpen(!isNavOpen)}>
						<Menu size={24} />
					</motion.button>
				)}
			</AnimatePresence>

			{/* Desktop Navigation - Always visible on md and up */}
			<div className="hidden md:flex w-24 h-screen bg-gray-100 text-gray-800 p-4 border-r border-gray-200 shadow-md flex-col">
				<div className="w-full">
					<button
						data-tooltip-id="nav-tooltip"
						data-tooltip-content="Logout"
						className="w-full flex items-center p-2 text-red-600 hover:bg-red-100 rounded justify-center h-10"
						onClick={handleLogout}>
						<LogOut size={16} />
					</button>
				</div>
				<nav className="flex-1 flex items-center">
					<ul className="space-y-4 w-full">
						<li
							data-tooltip-id="nav-tooltip"
							data-tooltip-content="Dashboard"
							onClick={() => handleNavItemClick("/dashboard")}
							className={`p-2 rounded cursor-pointer flex items-center h-10 justify-center hover:bg-gray-200 ${
								location.pathname.startsWith("/dashboard")
									? "bg-gray-300"
									: ""
							}`}>
							<BarChart size={16} />
						</li>
						<li
							data-tooltip-id="nav-tooltip"
							data-tooltip-content="Talk"
							onClick={() => handleNavItemClick("/therapy-home")}
							className={`p-2 rounded cursor-pointer flex items-center h-10 justify-center hover:bg-gray-200 ${
								location.pathname.startsWith("/therapy")
									? "bg-gray-300"
									: ""
							}`}>
							<MessageSquare size={16} />
						</li>
						<li
							data-tooltip-id="nav-tooltip"
							data-tooltip-content="Write"
							onClick={() => handleNavItemClick("/journal")}
							className={`p-2 rounded cursor-pointer flex items-center h-10 justify-center hover:bg-gray-200 ${
								location.pathname.startsWith("/journal")
									? "bg-gray-300"
									: ""
							}`}>
							<BookOpen size={16} />
						</li>
						<li
							data-tooltip-id="nav-tooltip"
							data-tooltip-content="FAQ"
							onClick={() => handleNavItemClick("/faq")}
							className={`p-2 rounded cursor-pointer flex items-center h-10 justify-center hover:bg-gray-200 ${
								location.pathname.startsWith("/faq")
									? "bg-gray-300"
									: ""
							}`}>
							<HelpCircle size={19} />
						</li>
					</ul>
				</nav>
				<li
					data-tooltip-id="nav-tooltip"
					data-tooltip-content="Settings"
					onClick={() => handleNavItemClick("/settings")}
					className={`p-2 rounded cursor-pointer flex items-center h-10 justify-center hover:bg-gray-200 ${
						location.pathname.startsWith("/settings")
							? "bg-gray-300"
							: ""
					}`}>
					<SettingsIcon size={16} />
				</li>
				<Tooltip id="nav-tooltip" place="right" className="z-[9999]" />
			</div>

			{/* Mobile Navigation */}
			<AnimatePresence>
				{isNavOpen && (
					<motion.div
						initial={{ x: "-100%" }}
						animate={{ x: 0 }}
						exit={{ x: "-100%" }}
						transition={{
							type: "spring",
							bounce: 0.1,
							duration: 0.3,
						}}
						className="fixed md:hidden w-64 h-screen bg-gray-100 text-gray-800 p-4 border-r border-gray-200 shadow-md flex flex-col z-40">
						<nav className="flex-1 flex items-center">
							<ul className="space-y-4 w-full">
								<li
									onClick={() =>
										handleNavItemClick("/dashboard")
									}
									className={`p-2 rounded cursor-pointer flex items-center h-10 justify-center gap-2 hover:bg-gray-200 ${
										location.pathname.startsWith(
											"/dashboard"
										)
											? "bg-gray-300"
											: ""
									}`}>
									<BarChart size={16} />
									Dashboard
								</li>
								<li
									onClick={() =>
										handleNavItemClick("/therapy-home")
									}
									className={`p-2 rounded cursor-pointer flex items-center h-10 justify-center gap-2 hover:bg-gray-200 ${
										location.pathname.startsWith("/therapy")
											? "bg-gray-300"
											: ""
									}`}>
									<MessageSquare size={16} />
									Talk
								</li>
								<li
									onClick={() =>
										handleNavItemClick("/journal")
									}
									className={`p-2 rounded cursor-pointer flex items-center h-10 justify-center gap-2 hover:bg-gray-200 ${
										location.pathname.startsWith("/journal")
											? "bg-gray-300"
											: ""
									}`}>
									<BookOpen size={16} />
									Write
								</li>
								<li
									onClick={() => handleNavItemClick("/faq")}
									className={`p-2 rounded cursor-pointer flex items-center h-10 justify-center gap-2 hover:bg-gray-200 ${
										location.pathname.startsWith("/faq")
											? "bg-gray-300"
											: ""
									}`}>
									<HelpCircle size={19} />
									FAQ
								</li>
								<li
									onClick={() =>
										handleNavItemClick("/settings")
									}
									className="p-2 rounded cursor-pointer flex items-center h-10 justify-center gap-2 hover:bg-gray-200">
									<SettingsIcon size={16} />
									Settings
								</li>
								<li
									className="p-2 rounded cursor-pointer flex items-center h-10 justify-center gap-2 hover:bg-gray-200 text-red-600 hover:bg-red-200"
									onClick={handleLogout}>
									<LogOut size={16} />
									Logout
								</li>
							</ul>
						</nav>
					</motion.div>
				)}
			</AnimatePresence>

			{/* Overlay */}
			<AnimatePresence>
				{isNavOpen && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 0.5 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.2 }}
						className="fixed inset-0 bg-black md:hidden z-30"
						onClick={() => setIsNavOpen(false)}
					/>
				)}
			</AnimatePresence>
		</>
	);

	if (loading) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
			</div>
		);
	}

	// Show auth if no user
	if (!user) {
		return <Auth />;
	}

	// Show shutdown notice for all authenticated users
	return <ShutdownNotice />;
};

export default App;
